import React, { useEffect, useRef, useState } from "react";
import { FileText, Paperclip, Send, X } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";

const MAX_QUERY_LENGTH = 1000; // matches ChatRequestSerializer on the backend

/**
 * Question input with document attachment capability.
 */
const CopilotComposer = ({
    onSend,
    disabled,
    placeholder = "Ask about leave, payslips, attendance or holidays…",
    compact = false,
    autoFocus = false,
}) => {
    const [value, setValue] = useState("");
    const [attachment, setAttachment] = useState(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setAttachment({
                name: file.name,
                size: file.size,
                type: file.type,
                content: event.target?.result || "",
            });
        };

        // For plain text files, read as text; for binary like PDF/Image, read summary note
        if (file.type.includes("text") || file.name.endsWith(".txt") || file.name.endsWith(".csv")) {
            reader.readAsText(file);
        } else {
            setAttachment({
                name: file.name,
                size: file.size,
                type: file.type,
                content: `Binary Document File (${file.type || 'Document'}, ${(file.size / 1024).toFixed(1)} KB)`,
            });
        }

        // Reset input value so re-selecting same file triggers onChange
        e.target.value = "";
    };

    const removeAttachment = () => {
        setAttachment(null);
    };

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, compact ? 88 : 128)}px`;
    }, [value, compact]);

    useEffect(() => {
        if (autoFocus) textareaRef.current?.focus();
    }, [autoFocus]);

    const submit = () => {
        const query = value.trim();
        if ((!query && !attachment) || disabled) return;

        onSend(query, attachment);
        setValue("");
        setAttachment(null);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
        }
    };

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {/* Hidden File Input */}
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv"
                onChange={handleFileSelect}
            />

            {/* Document Attachment Badge Preview */}
            {attachment && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-sidebar/30 bg-sidebar/5 px-2.5 py-1.5 text-xs text-foreground animate-fadeIn">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-sidebar" />
                        <span className="truncate font-medium">{attachment.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                            ({(attachment.size / 1024).toFixed(0)} KB)
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={removeAttachment}
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Remove file attachment"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
                className={cn(
                    "flex items-center gap-2 rounded-xl border border-input bg-background transition-colors",
                    "focus-within:border-sidebar-primary focus-within:ring-1 focus-within:ring-sidebar",
                    compact ? "px-2.5 py-1.5" : "px-3 py-2"
                )}
            >
                {/* Paperclip / File Upload Button */}
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                    title="Upload Document / File"
                    className={cn(
                        "flex shrink-0 items-center justify-center rounded-lg transition-all",
                        compact ? "h-7 w-7" : "h-8 w-8",
                        attachment
                            ? "bg-sidebar text-white shadow-sm"
                            : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                    )}
                >
                    <Paperclip className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
                </button>

                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={value}
                    maxLength={MAX_QUERY_LENGTH}
                    onChange={(event) => setValue(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        attachment
                            ? `Document attached: ${attachment.name}. Add instructions…`
                            : placeholder
                    }
                    className={cn(
                        "flex-1 resize-none bg-transparent py-1.5 leading-normal text-foreground outline-none my-auto",
                        "placeholder:text-muted-foreground",
                        compact ? "text-xs" : "text-sm"
                    )}
                />

                {/* Send Button */}
                <button
                    type="submit"
                    disabled={disabled || (!value.trim() && !attachment)}
                    title="Send"
                    className={cn(
                        "flex shrink-0 items-center justify-center rounded-lg transition-colors",
                        compact ? "h-7 w-7" : "h-8 w-8",
                        disabled || (!value.trim() && !attachment)
                            ? "cursor-not-allowed bg-muted text-muted-foreground"
                            : "bg-sidebar text-white hover:bg-sidebar-primary"
                    )}
                >
                    <Send className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
                </button>
            </form>
        </div>
    );
};

export default CopilotComposer;
